import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  BatchResultsResponse,
  MessageResponse,
  SessionResultResponse,
} from '../types';
import { getBatchResults, exportBatchCsv } from '../api';

const MESSAGE_TYPE_STYLES: Record<string, { label: string; color: string; bg: string }> = {
  ActionMessage: { label: 'Action', color: '#e65100', bg: '#fff3e0' },
  DataMessage: { label: 'Data', color: '#1565c0', bg: '#e3f2fd' },
  CommandMessage: { label: 'Command', color: '#6a1b9a', bg: '#f3e5f5' },
  ControlMessage: { label: 'Control', color: '#546e7a', bg: '#eceff1' },
};

const getMessageTypeStyle = (message: MessageResponse) => {
  if (message.sender === 'analytics') {
    return { label: message.message_type || 'Analytics', color: '#f57f17', bg: '#fffde7' };
  }
  return MESSAGE_TYPE_STYLES[message.message_type] ?? null;
};

const CollapsibleMetadata: React.FC<{ metadata: Record<string, unknown> }> = ({ metadata }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setExpanded(!expanded);
        }}
        style={{
          background: 'none',
          border: '1px solid #ccc',
          borderRadius: 4,
          padding: '2px 8px',
          fontSize: 11,
          cursor: 'pointer',
          color: '#555',
        }}
      >
        {expanded ? '▾ Hide metadata' : '▸ Show metadata'}
      </button>
      {expanded && (
        <pre
          style={{
            marginTop: 4,
            padding: 8,
            background: '#f5f5f5',
            borderRadius: 4,
            fontSize: 11,
            lineHeight: 1.4,
            overflow: 'auto',
            maxHeight: 300,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {JSON.stringify(metadata, null, 2)}
        </pre>
      )}
    </div>
  );
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

const BatchResults: React.FC = () => {
  const { batchId, sessionId } = useParams<{ batchId: string; sessionId?: string }>();
  const navigate = useNavigate();
  const [results, setResults] = useState<BatchResultsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());
  const [showAllMessages, setShowAllMessages] = useState(true);
  const sessionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const onBack = useCallback(() => navigate('/batches'), [navigate]);

  const fetchResults = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError('');
    try {
      const data = await getBatchResults(batchId);
      setResults(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch results';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    if (!sessionId || !results) return;
    const exists = results.sessions.some((s) => s.session_id === sessionId);
    if (!exists) return;
    setExpandedSessions((prev) => {
      if (prev.has(sessionId)) return prev;
      const next = new Set(prev);
      next.add(sessionId);
      return next;
    });
    sessionRefs.current.get(sessionId)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [sessionId, results]);

  const handleExportCsv = async () => {
    if (!batchId) return;
    try {
      const blob = await exportBatchCsv(batchId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch-${batchId.slice(0, 8)}-results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to export CSV';
      setError(message);
    }
  };

  const toggleSession = (toggledSessionId: string) => {
    const wasExpanded = expandedSessions.has(toggledSessionId);
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (wasExpanded) next.delete(toggledSessionId);
      else next.add(toggledSessionId);
      return next;
    });
    if (!batchId) return;
    if (wasExpanded && sessionId === toggledSessionId) {
      navigate(`/batches/${batchId}`, { replace: true });
    } else if (!wasExpanded) {
      navigate(`/batches/${batchId}/sessions/${toggledSessionId}`, { replace: true });
    }
  };

  const expandAll = () => {
    if (results) {
      setExpandedSessions(new Set(results.sessions.map((s) => s.session_id)));
    }
  };

  const collapseAll = () => {
    setExpandedSessions(new Set());
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'completed':
        return '#4caf50';
      case 'running':
        return '#2196f3';
      case 'failed':
        return '#f44336';
      default:
        return '#9e9e9e';
    }
  };

  const getSenderColor = (sender: string): string => {
    switch (sender) {
      case 'user':
        return '#e9fff1';
      case 'assistant':
        return '#eef4ff';
      case 'analytics':
        return '#fff9e6';
      default:
        return '#f5f5f5';
    }
  };

  const renderMessage = (message: MessageResponse) => {
    const typeStyle = getMessageTypeStyle(message);
    const isTextMessage = message.message_type === 'TextMessage' && !typeStyle;

    const bgColor = typeStyle ? typeStyle.bg : getSenderColor(message.sender);
    const borderColor = typeStyle ? typeStyle.color + '40' : '#e0e0e0';

    return (
      <div
        key={message.id}
        style={{
          padding: 10,
          borderRadius: 8,
          background: bgColor,
          border: `1px solid ${borderColor}`,
          borderLeft: typeStyle ? `3px solid ${typeStyle.color}` : undefined,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: '#666',
            marginBottom: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
              {message.sender}
            </span>
            {typeStyle && (
              <span
                style={{
                  padding: '1px 6px',
                  fontSize: 10,
                  borderRadius: 8,
                  background: typeStyle.color,
                  color: 'white',
                  fontWeight: 500,
                }}
              >
                {typeStyle.label}
              </span>
            )}
            {isTextMessage ? null : !typeStyle && (
              <span
                style={{
                  padding: '1px 6px',
                  fontSize: 10,
                  borderRadius: 8,
                  background: '#9e9e9e',
                  color: 'white',
                  fontWeight: 500,
                }}
              >
                {message.message_type}
              </span>
            )}
          </div>
          <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
        </div>
        {message.content && (
          <div style={{ fontSize: 13, lineHeight: 1.4 }}>{message.content}</div>
        )}
        {message.metadata &&
          Object.keys(message.metadata).length > 0 && (
            <CollapsibleMetadata metadata={message.metadata} />
          )}
      </div>
    );
  };

  const renderSession = (session: SessionResultResponse) => {
    const isExpanded = expandedSessions.has(session.session_id);
    const textMessages = session.messages.filter(
      (m) => m.message_type === 'TextMessage' && m.content
    );
    const displayMessages = showAllMessages ? session.messages : textMessages;
    const llmConfig = (session.llm_config ?? {}) as Record<string, unknown>;
    const isVoice = llmConfig.voice === true;
    const audioRef = (llmConfig.audio_ref ?? null) as Record<string, unknown> | null;
    const recordingUrl =
      audioRef && typeof audioRef.recording_url === 'string'
        ? audioRef.recording_url
        : null;
    const phoneNumber =
      audioRef && typeof audioRef.phone_number === 'string'
        ? audioRef.phone_number
        : null;

    return (
      <div
        key={session.session_id}
        ref={(el) => {
          if (el) sessionRefs.current.set(session.session_id, el);
          else sessionRefs.current.delete(session.session_id);
        }}
        style={{
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          marginBottom: 8,
          overflow: 'hidden',
        }}
      >
        <div
          onClick={() => toggleSession(session.session_id)}
          style={{
            padding: 12,
            cursor: 'pointer',
            background: isExpanded ? '#f8f9fa' : '#fff',
            borderBottom: isExpanded ? '1px solid #e0e0e0' : 'none',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14 }}>{isExpanded ? '▼' : '▶'}</span>
              <span
                style={{
                  padding: '2px 8px',
                  fontSize: 11,
                  borderRadius: 10,
                  background: getStatusColor(session.status),
                  color: 'white',
                }}
              >
                {session.status}
              </span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>
                Session {session.session_id.slice(0, 8)}
              </span>
              {isVoice && (
                <span
                  style={{
                    padding: '2px 8px',
                    fontSize: 11,
                    borderRadius: 10,
                    background: '#6f42c1',
                    color: 'white',
                  }}
                >
                  🎙 Voice
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              Turns: {session.turn_count}/{session.max_turns} | Messages:{' '}
              {textMessages.length}
              {showAllMessages &&
                session.messages.length !== textMessages.length &&
                ` (${session.messages.length} total)`}
              {session.started_at && ` | Started: ${formatDate(session.started_at)}`}
              {session.ended_at && ` | Ended: ${formatDate(session.ended_at)}`}
            </div>
          </div>
          {session.error && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                background: '#fff0f0',
                color: '#8b0000',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              Error: {session.error}
            </div>
          )}
        </div>
        {isExpanded && (
          <div style={{ padding: 12 }}>
            {isVoice && audioRef && (
              <div
                style={{
                  marginBottom: 12,
                  padding: 8,
                  background: '#f3f0fa',
                  borderRadius: 4,
                  fontSize: 12,
                }}
              >
                <div style={{ marginBottom: 6, color: '#555' }}>
                  Voice call recording{phoneNumber ? ` (dialed ${phoneNumber})` : ''}
                </div>
                {recordingUrl ? (
                  <audio
                    controls
                    src={recordingUrl}
                    style={{ width: '100%' }}
                  />
                ) : (
                  <div style={{ color: '#888' }}>
                    Recording link not available yet.
                  </div>
                )}
              </div>
            )}
            {displayMessages.length === 0 ? (
              <div style={{ color: '#666', fontSize: 13, textAlign: 'center' }}>
                No messages in this session.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {displayMessages.map(renderMessage)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        Loading results...
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <button
          onClick={onBack}
          style={{
            marginBottom: 16,
            padding: '8px 16px',
            background: '#e0e0e0',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          Back to Batches
        </button>
        <div
          style={{
            padding: 20,
            background: '#fff0f0',
            color: '#8b0000',
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  if (!results) {
    return null;
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              padding: '8px 16px',
              background: '#e0e0e0',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Back
          </button>
          <h2 style={{ margin: 0, fontSize: 18 }}>{results.name}</h2>
          <span
            style={{
              padding: '4px 10px',
              fontSize: 12,
              borderRadius: 10,
              background: getStatusColor(results.status),
              color: 'white',
            }}
          >
            {results.status}
          </span>
        </div>
        <button
          onClick={handleExportCsv}
          className="button button-primary"
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <span>Export CSV</span>
        </button>
      </div>

      <div
        style={{
          background: '#f8f9fa',
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          display: 'flex',
          gap: 24,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Total Sessions</div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>{results.seed_count}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Completed</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#4caf50' }}>
            {results.completed_count}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666' }}>Failed</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#f44336' }}>
            {results.failed_count}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: '#666' }}>
            Created: {formatDate(results.created_at)}
          </div>
          {results.started_at && (
            <div style={{ fontSize: 11, color: '#666' }}>
              Started: {formatDate(results.started_at)}
            </div>
          )}
          {results.ended_at && (
            <div style={{ fontSize: 11, color: '#666' }}>
              Ended: {formatDate(results.ended_at)}
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>
          Sessions ({results.sessions.length})
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={showAllMessages}
              onChange={(e) => setShowAllMessages(e.target.checked)}
            />
            Show all message types
          </label>
          <div style={{ width: 1, height: 16, background: '#ccc' }} />
          <button
            onClick={expandAll}
            style={{
              padding: '4px 8px',
              fontSize: 12,
              background: '#e0e0e0',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            style={{
              padding: '4px 8px',
              fontSize: 12,
              background: '#e0e0e0',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Collapse All
          </button>
        </div>
      </div>

      {results.sessions.map(renderSession)}
    </div>
  );
};

export default BatchResults;
